// braveSearchService.js - Brave Search via Cloudflare Worker Proxy
// Updated to use proxy endpoint to avoid CORS issues

// Cloudflare Worker proxy endpoint
const PROXY_URL = 'https://brave-search-proxy.reza-dizadji.workers.dev';

class BraveSearchService {
  /**
   * Search the web using Brave Search API via Cloudflare Worker proxy
   * @param {string} query - Search query
   * @param {object} options - Search options (count, freshness, etc)
   * @returns {Promise<object>} Search results
   */
  async search(query, options = {}) {
    const {
      count = 10,
      offset = 0,
      freshness = null,
      safesearch = 'moderate'
    } = options;

    try {
      // Build URL with params
      const params = new URLSearchParams({
        q: query,
        count: count.toString()
      });

      if (offset > 0) params.append('offset', offset.toString());
      if (freshness) params.append('freshness', freshness);
      params.append('safesearch', safesearch);

      const url = `${PROXY_URL}?${params.toString()}`;
      
      console.log('🔍 Brave Search via proxy:', query);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // Parse results
      const results = (data.web?.results || []).map(result => ({
        title: result.title,
        url: result.url,
        description: result.description,
        age: result.age,
        language: result.language
      }));

      console.log(`✅ Brave Search: Found ${results.length} results`);

      return {
        success: true,
        query: query,
        results: results,
        total: data.web?.results?.length || 0
      };

    } catch (error) {
      console.error('❌ Brave Search error:', error);
      return {
        success: false,
        error: error.message,
        results: []
      };
    }
  }

  /**
   * Search with specific freshness (e.g., past day, week)
   */
  async searchRecent(query, days = 1) {
    const freshness = days === 1 ? 'pd' : 
                     days === 7 ? 'pw' : 
                     days === 30 ? 'pm' : 
                     days === 365 ? 'py' : null;
    
    return this.search(query, { freshness });
  }

  /**
   * Get news results (recent + news-focused)
   */
  async searchNews(query, count = 5) {
    return this.search(`${query} news`, { 
      count, 
      freshness: 'pd' // Past day
    });
  }
}

const braveSearchService = new BraveSearchService();
export default braveSearchService;
